import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MapPin } from "lucide-react";
import { scrollToId } from "@/lib/content";
import { useTexts, useExtras } from "@/lib/texts";

const EASE = [0.16, 1, 0.3, 1];

const RevealLine = ({ children, delay = 0, className = "" }) => (
    <span className={`block overflow-hidden ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const HERO_STATS = [
    { value: "30+", label: "Anni di Storia" },
    { value: "180+", label: "Progetti Completati" },
    { value: "100%", label: "Attenzione al Progetto" },
];

export const Hero = () => {
    const frameRef = useRef(null);
    const texts = useTexts();
    const { heroImage } = useExtras();

    const { scrollYProgress } = useScroll({
        target: frameRef,
        offset: ["start end", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
    const frameY = useTransform(scrollYProgress, [0, 1], [0, 60]);

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative overflow-hidden bg-[#F9F7F2] pt-[72px]"
        >
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-16 pt-14 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-16 lg:pb-24 lg:pt-20">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
                        className="mb-8 inline-flex items-center gap-3"
                    >
                        <span className="h-px w-10 bg-[#E75C15]" />
                        <span className="font-mono-alt text-xs uppercase tracking-[0.3em] text-stone-500">
                            {texts.hero_overline}
                        </span>
                    </motion.div>

                    <h1 className="font-display text-4xl font-light leading-[1.05] tracking-tight text-stone-900 sm:text-6xl lg:text-7xl">
                        <RevealLine delay={0.45}>{texts.hero_title_1}</RevealLine>
                        <RevealLine delay={0.58}>{texts.hero_title_2}</RevealLine>
                        <RevealLine delay={0.71}>
                            <em className="font-semibold italic text-[#B8430D]">
                                {texts.hero_title_3}
                            </em>
                        </RevealLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
                        className="mt-8 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg"
                    >
                        {texts.hero_paragraph}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-cta-portfolio"
                            onClick={() => scrollToId("portfolio")}
                            className="group inline-flex items-center gap-2 rounded-full bg-[#1C1917] px-7 py-4 text-sm font-medium text-stone-100 transition-colors duration-300 hover:bg-[#B8430D]"
                        >
                            {texts.hero_cta_portfolio}
                            <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                        </button>
                        <button
                            data-testid="hero-cta-contact"
                            onClick={() => scrollToId("contatti")}
                            className="inline-flex items-center gap-2 rounded-full border border-stone-900/20 px-7 py-4 text-sm font-medium text-stone-900 transition-colors duration-300 hover:border-[#E75C15] hover:text-[#B8430D]"
                        >
                            {texts.hero_cta_contact}
                        </button>
                    </motion.div>
                </div>

                <div className="relative lg:col-span-5" ref={frameRef}>
                    <motion.div
                        initial={{ clipPath: "inset(100% 0 0 0)" }}
                        animate={{ clipPath: "inset(0% 0 0 0)" }}
                        transition={{ duration: 1.4, delay: 0.55, ease: EASE }}
                        className="relative"
                        style={{ y: frameY }}
                    >
                        <div className="relative aspect-[3/4] overflow-hidden rounded-t-[160px] border border-[#E75C15]/40 p-2">
                            <div className="relative h-full w-full overflow-hidden rounded-t-[152px]">
                                <motion.img
                                    src={heroImage}
                                    alt="Finestra ad arco in pietra su paesaggio toscano"
                                    fetchPriority="high"
                                    decoding="async"
                                    className="h-full w-full object-cover"
                                    style={{ y: imgY, scale: 1.18 }}
                                    data-testid="hero-image"
                                />
                            </div>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
                            data-testid="hero-stats-card"
                            className="absolute inset-x-3 bottom-3 flex justify-around divide-x divide-[#E75C15]/30 rounded-lg border border-[#E75C15]/30 bg-[rgba(28,25,23,0.92)] px-2 py-3 backdrop-blur-md"
                        >
                            {HERO_STATS.map((s) => (
                                <div key={s.label} className="px-3 text-center sm:px-4">
                                    <div className="font-display text-xl font-semibold text-[#E75C15] sm:text-2xl">
                                        {s.value}
                                    </div>
                                    <div className="mt-1 font-mono-alt text-[9px] uppercase leading-snug tracking-[0.12em] text-stone-400">
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="mx-auto flex max-w-7xl items-center justify-between px-4 pb-6 sm:px-8 lg:px-16"
            >
                <span className="inline-flex items-center gap-2 font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-400">
                    <MapPin className="h-3.5 w-3.5 text-[#C24E0F]" />
                    Pietrasanta · Versilia · Toscana
                </span>
                <span className="font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-400">
                    Ristrutturazioni · Recupero · Nuove Costruzioni
                </span>
            </motion.div>
        </section>
    );
};
